import { atom } from 'jotai';
import type { LatLng } from '@/core/utils';
import { computeCone } from '../compute/computeCone';
import { resolveSettings, type SettingsInput } from '../model/settings';
import type { GlideMode } from '../model/types';
import { inspectionAtom, modeAtom, originAtom, resultAtom, settingsInputAtom, statusAtom } from './atoms';

/** Controller of the computation in flight; aborting it discards its result. */
const runAtom = atom<AbortController | null>(null);

/** Computes the cone for an origin in the current mode, superseding any running computation. */
export const computeConeAtom = atom(null, async (get, set, origin: LatLng) => {
  get(runAtom)?.abort();
  const run = new AbortController();
  set(runAtom, run);

  const mode = get(modeAtom);
  set(originAtom, { ...origin, mode });
  set(inspectionAtom, null);
  set(statusAtom, { kind: 'loading' });
  try {
    const result = await computeCone({
      origin,
      mode,
      settings: resolveSettings(get(settingsInputAtom)),
      signal: run.signal,
      onSearching: (size) => set(statusAtom, { kind: 'searching', size }),
    });
    set(resultAtom, result);
    set(inspectionAtom, null);
    set(statusAtom, { kind: 'done' });
  } catch (error) {
    if (run.signal.aborted) return;
    console.error(error);
    set(statusAtom, { kind: 'error', error });
  }
});

/** Re-runs the computation for the current origin, if any. */
const recomputeAtom = atom(null, (get, set) => {
  const origin = get(originAtom);
  if (origin) void set(computeConeAtom, origin);
});

/** Switches fly/hike, re-using the clicked point in its new role. */
export const setModeAtom = atom(null, (get, set, mode: GlideMode) => {
  set(modeAtom, mode);
  if (get(originAtom)) set(recomputeAtom);
  else set(statusAtom, { kind: 'idle' });
});

/** Stores edited settings and recomputes. */
export const commitSettingsAtom = atom(null, (_get, set, update: Partial<SettingsInput>) => {
  set(settingsInputAtom, (prev) => ({ ...prev, ...update }));
  set(recomputeAtom);
});

export const clearConeAtom = atom(null, (get, set) => {
  get(runAtom)?.abort();
  set(runAtom, null);
  set(originAtom, null);
  set(resultAtom, null);
  set(inspectionAtom, null);
  set(statusAtom, { kind: 'idle' });
});
